import { makeStyles } from "@material-ui/core/styles";
import { COLORS } from "./theme";

export const useStyles = makeStyles(() => ({
  root: {
    fontFamily: "'DM Sans', system-ui, sans-serif",
    color: COLORS.ink,
    fontSize: 14,
    background: COLORS.page,
    minHeight: "100vh",
  },
  mono: {
    fontFamily: "'DM Mono', ui-monospace, monospace",
    fontFeatureSettings: '"tnum"',
  },

  /* topbar */
  topbar: {
    height: 52,
    background: COLORS.brandBar,
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    gap: 16,
    color: "#3B2A08",
  },
  topbarSite: { fontSize: 17, fontWeight: 500, letterSpacing: "-0.01em" },
  bell: {
    position: "relative",
    border: 0,
    background: "none",
    color: "#3B2A08",
    fontSize: 19,
    cursor: "pointer",
    width: 34,
    height: 34,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    "&:hover": { background: "rgba(59,42,8,.1)" },
  },
  bellDot: {
    position: "absolute",
    top: 1,
    right: 0,
    background: "#B3261E",
    color: "#fff",
    borderRadius: 9,
    font: "500 9.5px/1 'DM Mono', monospace",
    minWidth: 15,
    height: 15,
    padding: "0 4px",
    display: "grid",
    placeItems: "center",
    border: `1.5px solid ${COLORS.brandBar}`,
  },
  who: { fontSize: 12.5, opacity: 0.85, marginLeft: "auto" },

  /* shell */
  shell: { display: "flex", minHeight: "calc(100vh - 52px)" },
  rail: {
    width: 56,
    background: COLORS.card,
    borderRight: `1px solid ${COLORS.line}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "14px 0",
    gap: 4,
    flex: "0 0 56px",
  },
  railBtn: {
    width: 36,
    height: 36,
    border: 0,
    background: "none",
    borderRadius: 8,
    color: COLORS.ink3,
    fontSize: 18,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    "&:hover": { background: COLORS.page, color: COLORS.ink2 },
  },
  railBtnOn: { background: COLORS.brandSoft, color: COLORS.brand },
  main: { flex: 1, minWidth: 0, padding: "20px 24px 60px" },

  crumbs: {
    fontSize: 11.5,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: COLORS.ink3,
    marginBottom: 14,
    "& a": { color: COLORS.brand, textDecoration: "none", cursor: "pointer" },
    "& span": { margin: "0 7px", opacity: 0.5 },
  },
  head: {
    display: "flex",
    alignItems: "flex-end",
    gap: 16,
    marginBottom: 18,
    flexWrap: "wrap",
    "& h1": { fontSize: 26, fontWeight: 500, letterSpacing: "-0.02em", margin: 0 },
    "& .sub": { fontSize: 13, color: COLORS.ink3, paddingBottom: 4 },
  },
  spacer: { flex: 1 },
  ghost: {
    border: `1px solid ${COLORS.line}`,
    background: COLORS.card,
    borderRadius: 8,
    padding: "7px 13px",
    font: "500 12.5px/1 'DM Sans', sans-serif",
    color: COLORS.ink2,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    textTransform: "none",
    "&:hover": { borderColor: "#D3D7DC", color: COLORS.ink, background: COLORS.card },
  },

  roles: {
    display: "flex",
    gap: 2,
    background: "#ECEDEF",
    padding: 3,
    borderRadius: 10,
    marginBottom: 6,
    width: "fit-content",
  },
  roleBtn: {
    border: 0,
    background: "none",
    borderRadius: 7,
    padding: "7px 15px",
    cursor: "pointer",
    font: "500 13px/1 'DM Sans', sans-serif",
    color: COLORS.ink2,
    display: "flex",
    alignItems: "center",
    gap: 7,
    textTransform: "none",
    minHeight: "auto",
  },
  roleBtnOn: {
    background: COLORS.card,
    color: COLORS.ink,
    boxShadow: "0 1px 2px rgba(0,0,0,.07)",
  },
  roleN: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    background: "#DFE2E5",
    color: COLORS.ink2,
    borderRadius: 20,
    padding: "1px 6px",
  },
  roleNOn: { background: COLORS.brandSoft, color: COLORS.brand },
  tabdot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#B3261E",
    flex: "0 0 6px",
  },
  rolenote: {
    fontSize: 11.5,
    color: COLORS.ink3,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  toolbar: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 12,
    flexWrap: "wrap",
  },
  search: {
    position: "relative",
    "& input": {
      border: `1px solid ${COLORS.line}`,
      background: COLORS.card,
      borderRadius: 8,
      padding: "8px 12px 8px 31px",
      font: "400 13px 'DM Sans', sans-serif",
      width: 250,
      color: COLORS.ink,
      outline: "none",
    },
    "& input:focus": {
      outline: `2px solid ${COLORS.brand}`,
      outlineOffset: -1,
      borderColor: "transparent",
    },
    "& .searchIcon": {
      position: "absolute",
      left: 10,
      top: "50%",
      transform: "translateY(-50%)",
      color: COLORS.ink3,
      fontSize: 15,
      pointerEvents: "none",
    },
  },
  hint: { fontSize: 12.5, color: COLORS.ink3 },

  /* table */
  tablewrap: {
    background: COLORS.card,
    border: `1px solid ${COLORS.line}`,
    borderRadius: 12,
    // overflow: "hidden",
    overflowX: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: COLORS.ink3,
    padding: "11px 14px",
    background: "#FCFCFB",
    borderBottom: `1px solid ${COLORS.line}`,
    whiteSpace: "nowrap",
  },
  thlab: { display: "inline-flex", alignItems: "center", gap: 6 },
  thlabIcon: { fontSize: 14, opacity: 0.75 },
  td: {
    padding: "13px 14px",
    borderBottom: `1px solid ${COLORS.line2}`,
    verticalAlign: "middle",
  },
  tr: {
    "&:hover td": { background: "#FCFCFA" },
  },
  trFresh: {
    "& td": { background: "#FFFCF6" },
    "&:hover td": { background: "#FFF9EE" },
    "& td:first-child": { boxShadow: `inset 3px 0 0 ${COLORS.amber}` },
  },
  trCopy: {
    "& td": { background: "#FBFAFD" },
    "&:hover td": { background: "#F7F5FC" },
    "& td:first-child": { boxShadow: "inset 3px 0 0 #B9A5DC" },
  },
  trDone: {
    "& td": { background: "#FCFDFC" },
    "&:hover td": { background: "#F7FBF8" },
    "& td:first-child": { boxShadow: "inset 3px 0 0 #BEDCCB" },
  },

  pnum: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13.5,
    fontWeight: 500,
    letterSpacing: "-0.01em",
  },
  pcust: { fontSize: 13, color: COLORS.ink2, marginTop: 1 },
  pbom: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11.5,
    color: COLORS.ink3,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  rtype: { fontSize: 13.5, lineHeight: 1.35 },
  rver: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11.5,
    color: COLORS.ink3,
    marginTop: 2,
  },
  plain: { fontFamily: "'DM Mono', monospace", fontSize: 12.5, color: COLORS.ink2 },
  plainNone: {
    color: COLORS.ink3,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12.5,
  },

  stage: { display: "flex", flexDirection: "column", gap: 5 },
  pips: { display: "flex", alignItems: "center", gap: 3 },
  pip: { height: 4, width: 22, borderRadius: 2, background: "#E3E6E9" },
  pipDone: { background: "#B9C2CB" },
  pipNow: { background: COLORS.blue },
  pipBack: { background: COLORS.amber },
  stageLabel: { display: "flex", alignItems: "center", gap: 6 },
  stageName: { fontSize: 12.5, fontWeight: 500 },
  stageNeed: {
    fontSize: 11.5,
    color: COLORS.ink3,
    marginTop: 3,
    display: "flex",
    alignItems: "flex-start",
    gap: 5,
    lineHeight: 1.4,
    "& b": { fontWeight: 500, color: COLORS.ink2 },
  },

  fb: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    cursor: "pointer",
    width: "fit-content",
    border: 0,
    background: "none",
    padding: 0,
    textAlign: "left",
    font: "inherit",
  },
  fbTop: { display: "flex", alignItems: "center", gap: 6 },
  fbNum: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  fbLab: { fontSize: 11.5, color: COLORS.ink3, whiteSpace: "nowrap" },
  fbBar: {
    width: 92,
    height: 3,
    borderRadius: 2,
    background: "#E9EBEE",
    overflow: "hidden",
  },
  fbFill: { height: "100%", borderRadius: 2 },
  fbSub: {
    fontSize: 10.5,
    color: COLORS.ink3,
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 1,
  },
  fbNone: { color: COLORS.ink3, fontSize: 12.5 },
  fbWarnNum: { color: COLORS.amber },
  fbDoneNum: { color: COLORS.green },
  newdot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: COLORS.amber,
    flex: "0 0 7px",
  },

  due: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 12.5,
    color: COLORS.ink2,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  dueLate: { color: COLORS.red, fontWeight: 500 },
  dueRel: { fontSize: 11, color: COLORS.ink3, marginTop: 1 },

  actwrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    justifyContent: "flex-end",
  },
  act: {
    border: 0,
    borderRadius: 8,
    padding: "8px 13px",
    cursor: "pointer",
    font: "500 12.5px/1 'DM Sans', sans-serif",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    textTransform: "none",
    minHeight: "auto",
  },
  actPrimary: {
    background: COLORS.brand,
    color: "#fff",
    "&:hover": { background: "#C97A0C" },
  },
  actWarn: {
    background: COLORS.amber,
    color: "#fff",
    "&:hover": { background: "#8A4C08" },
  },
  actGo: {
    background: COLORS.green,
    color: "#fff",
    "&:hover": { background: "#0F5533" },
  },
  actSecondary: {
    background: COLORS.card,
    border: `1px solid ${COLORS.line}`,
    color: COLORS.ink2,
    "&:hover": { borderColor: "#D3D7DC", color: COLORS.ink },
  },
  actDisabled: {
    background: "#E2E5E8",
    color: COLORS.ink3,
    cursor: "not-allowed",
    borderColor: "transparent",
    "&:hover": { background: "#E2E5E8" },
  },
  actWait: {
    background: "transparent",
    color: COLORS.ink3,
    fontWeight: 400,
    cursor: "default",
    paddingLeft: 0,
  },
  iconBtn: {
    width: 32,
    height: 32,
    border: `1px solid ${COLORS.line}`,
    background: COLORS.card,
    borderRadius: 8,
    color: COLORS.ink2,
    fontSize: 16,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    flex: "0 0 32px",
    "&:hover": {
      borderColor: "#D3D7DC",
      color: COLORS.ink,
      background: COLORS.page,
    },
  },
  more: {
    width: 30,
    height: 30,
    border: 0,
    background: "none",
    borderRadius: 6,
    color: COLORS.ink3,
    fontSize: 17,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    flex: "0 0 30px",
    "&:hover": { background: COLORS.page, color: COLORS.ink },
  },

  pager: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
    fontSize: 12.5,
    color: COLORS.ink3,
    "& .btns": { display: "flex", gap: 4, marginLeft: "auto" },
  },
  empty: {
    padding: "52px 20px",
    textAlign: "center",
    "& .bigIcon": { fontSize: 26, color: COLORS.ink3 },
    "& p": { marginTop: 8, fontSize: 13.5, color: COLORS.ink2 },
    "& span": { fontSize: 12.5, color: COLORS.ink3 },
  },
  badge: {
    fontSize: 10.5,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    borderRadius: 4,
    padding: "2px 7px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  badgeCopy: { background: COLORS.violetSoft, color: COLORS.violet },

  /* report view */
  reportShell: {
    display: "flex",
    height: "calc(100vh - 52px)",
  },
  doc: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    background: "#EFEFEC",
    padding: "22px 0 60px",
  },
  docbar: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: COLORS.card,
    borderBottom: `1px solid ${COLORS.line}`,
    margin: "-22px 0 22px",
    padding: "11px 22px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  back: {
    border: 0,
    background: "none",
    color: COLORS.ink2,
    font: "500 12.5px 'DM Sans', sans-serif",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 9px",
    borderRadius: 6,
    textTransform: "none",
    "&:hover": { background: COLORS.page, color: COLORS.ink },
  },
  dt: { fontSize: 13.5, fontWeight: 500 },
  dm: { fontSize: 12, color: COLORS.ink3, fontFamily: "'DM Mono', monospace" },
  sheet: {
    background: "#fff",
    maxWidth: 720,
    margin: "0 auto",
    padding: "52px 62px",
    borderRadius: 4,
    boxShadow: "0 1px 3px rgba(20,25,30,.09)",
    fontSize: 13.5,
    lineHeight: 1.75,
    color: "#22262B",
    "& h2": { fontSize: 15, fontWeight: 700, margin: "26px 0 9px", letterSpacing: "0.01em" },
    "& h2:first-child": { marginTop: 0 },
    "& p": { marginBottom: 13 },
    "& table": { width: "100%", borderCollapse: "collapse", margin: "14px 0 18px", fontSize: 12.5 },
    "& th, & td": { border: "1px solid #D8DBDF", padding: "6px 9px", textAlign: "left" },
    "& th": { background: "#F3F4F5", fontWeight: 500 },
    "& .mark": {
      background: "#FDF0D3",
      borderBottom: `2px solid ${COLORS.brand}`,
      cursor: "pointer",
      padding: "1px 0",
    },
    "& .mark.active": { background: "#F9DFA8" },
    "& .mark.resolved": { background: COLORS.greenSoft, borderBottomColor: COLORS.green },
  },

  panel: {
    width: 400,
    flex: "0 0 400px",
    background: COLORS.card,
    borderLeft: `1px solid ${COLORS.line}`,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  panelHead: {
    padding: "15px 18px 12px",
    borderBottom: `1px solid ${COLORS.line}`,
    "& h3": { fontSize: 14.5, fontWeight: 500, margin: 0 },
    "& .sub": { fontSize: 12, color: COLORS.ink3, marginTop: 3, lineHeight: 1.45 },
    "& .row": { display: "flex", alignItems: "center", gap: 9 },
  },
  panelBody: { flex: 1, overflowY: "auto", padding: "6px 18px 20px" },
  panelFoot: {
    borderTop: `1px solid ${COLORS.line}`,
    padding: "13px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 9,
    "& .msg": {
      fontSize: 12,
      color: COLORS.ink3,
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      lineHeight: 1.45,
    },
    "& .msg.block": { color: COLORS.amber },
  },

  legend: {
    display: "flex",
    gap: 14,
    padding: "11px 0 6px",
    fontSize: 11.5,
    color: COLORS.ink3,
    flexWrap: "wrap",
    "& span": { display: "flex", alignItems: "center", gap: 5 },
  },
  lg: {
    width: 14,
    height: 14,
    borderRadius: 4,
    display: "grid",
    placeItems: "center",
    fontSize: 9,
    flex: "0 0 14px",
  },
  lgD: { background: COLORS.greenSoft, color: COLORS.green },
  lgP: { background: COLORS.amberSoft, color: COLORS.amber },
  lgN: { background: "#EFF1F3", border: "1px solid #DDE1E5" },

  cgroup: {
    fontSize: 10.5,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: COLORS.ink2,
    background: COLORS.page,
    padding: "7px 11px",
    borderRadius: 6,
    margin: "15px 0 3px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 7,
    "& svg": { fontSize: 14, color: COLORS.ink3 },
  },
  citem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 2px",
    borderBottom: `1px solid ${COLORS.line2}`,
  },
  citemFlag: {
    background: "#FFFCF6",
    borderRadius: 6,
    padding: "10px 8px",
    margin: "0 -8px",
  },
  cbox: {
    width: 17,
    height: 17,
    borderRadius: 5,
    flex: "0 0 17px",
    marginTop: 1,
    display: "grid",
    placeItems: "center",
    fontSize: 10,
    padding: 0,
    "& svg": { fontSize: 12 },
  },
  cboxD: { background: COLORS.green, color: "#fff", border: 0 },
  cboxP: { background: COLORS.amber, color: "#fff", border: 0 },
  cboxN: { background: "#fff", border: "1.5px solid #D3D8DD" },
  cboxBtn: {
    cursor: "pointer",
    transition: "transform .1s",
    "&:hover": { transform: "scale(1.14)" },
  },
  ctext: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: COLORS.ink2,
    lineHeight: 1.45,
  },
  itemTools: { display: "flex", alignItems: "center", gap: 2, flex: "0 0 auto" },
  flagbtn: {
    border: 0,
    background: "none",
    borderRadius: 6,
    width: 28,
    height: 28,
    flex: "0 0 28px",
    cursor: "pointer",
    color: "#C2C8CE",
    fontSize: 15,
    display: "grid",
    placeItems: "center",
    position: "relative",
    "&:hover": { background: COLORS.amberSoft, color: COLORS.amber },
  },
  flagbtnOn: { color: COLORS.amber, background: COLORS.amberSoft },
  notebtn: {
    border: 0,
    background: "none",
    borderRadius: 6,
    width: 28,
    height: 28,
    flex: "0 0 28px",
    cursor: "pointer",
    color: "#C2C8CE",
    fontSize: 15,
    display: "grid",
    placeItems: "center",
    "&:hover": { background: COLORS.page, color: COLORS.ink2 },
  },
  notebtnHas: { color: COLORS.brand },
  notebtnMissing: { color: COLORS.red },
  cnote: {
    marginTop: 7,
    fontSize: 12.5,
    color: COLORS.ink2,
    background: COLORS.page,
    borderLeft: `2px solid ${COLORS.line}`,
    padding: "7px 10px",
    borderRadius: "0 6px 6px 0",
    "& .w": {
      display: "block",
      fontSize: 11,
      color: COLORS.ink3,
      marginTop: 3,
      fontFamily: "'DM Mono', monospace",
    },
  },
  err: {
    fontSize: 12,
    color: COLORS.red,
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginTop: 6,
  },
  editor: {
    marginTop: 8,
    "& textarea, & select, & input.field": {
      width: "100%",
      border: `1px solid ${COLORS.line}`,
      borderRadius: 8,
      padding: "8px 10px",
      font: "400 12.5px/1.5 'DM Sans', sans-serif",
      resize: "vertical",
      color: COLORS.ink,
      background: "#fff",
      outline: "none",
      "&:focus": {
        outline: `2px solid ${COLORS.brand}`,
        outlineOffset: -1,
        borderColor: "transparent",
      },
    },
    "& .row": {
      display: "flex",
      gap: 7,
      alignItems: "center",
      marginTop: 7,
      flexWrap: "wrap",
    },
    "& .sp": { flex: 1, minWidth: 0 },
  },
  field: {
    width: "100%",
    border: `1px solid ${COLORS.line}`,
    borderRadius: 8,
    padding: "8px 10px",
    font: "400 12.5px/1.5 'DM Sans', sans-serif",
    color: COLORS.ink,
    background: "#fff",
    outline: "none",
    "&:focus": {
      outline: `2px solid ${COLORS.brand}`,
      outlineOffset: -1,
      borderColor: "transparent",
    },
  },
  mini: {
    border: 0,
    borderRadius: 6,
    padding: "6px 11px",
    cursor: "pointer",
    font: "500 12px/1 'DM Sans', sans-serif",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    whiteSpace: "nowrap",
    flex: "0 0 auto",
    textTransform: "none",
    minHeight: "auto",
  },
  miniGo: { background: COLORS.ink, color: "#fff" },
  miniNo: { background: "none", color: COLORS.ink3 },

  cm: {
    border: `1px solid ${COLORS.line}`,
    borderRadius: 10,
    padding: "11px 12px",
    marginBottom: 9,
    background: COLORS.card,
    cursor: "pointer",
    "&:hover": { borderColor: "#D3D7DC" },
  },
  cmActive: {
    borderColor: COLORS.brand,
    boxShadow: `0 0 0 2px ${COLORS.brandSoft}`,
  },
  cmHeld: { background: "#FFFDF7", borderColor: "#F0DCB4" },
  cmFixed: { background: "#FAFDFB", borderColor: "#CFE6DA" },
  cmTop: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    marginBottom: 6,
    flexWrap: "wrap",
  },
  cmWho: {
    fontSize: 12.5,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    "& svg": { fontSize: 15, color: COLORS.ink3 },
  },
  cmWhen: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10.5,
    color: COLORS.ink3,
    marginLeft: "auto",
  },
  cmAnchor: {
    fontSize: 11.5,
    color: COLORS.brand,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 5,
  },
  cmBody: { fontSize: 13, color: COLORS.ink2, lineHeight: 1.5 },
  cmFoot: {
    marginTop: 9,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  tag: {
    fontSize: 10.5,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    borderRadius: 4,
    padding: "2px 7px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    "& svg": { fontSize: 12 },
  },
  tagVerify: { background: COLORS.amberSoft, color: COLORS.amber },
  tagApproval: { background: COLORS.violetSoft, color: COLORS.violet },
  tagNotsent: { background: COLORS.amberSoft, color: COLORS.amber },
  tagFixed: { background: COLORS.greenSoft, color: COLORS.green },
  ackbtn: {
    border: `1px solid ${COLORS.line}`,
    background: COLORS.card,
    borderRadius: 6,
    padding: "6px 11px",
    cursor: "pointer",
    font: "500 12px 'DM Sans', sans-serif",
    color: COLORS.ink2,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    textTransform: "none",
    "&:hover": { borderColor: COLORS.green, color: COLORS.green },
    "& svg": { fontSize: 14 },
  },
  ackdone: {
    fontSize: 12,
    color: COLORS.green,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    "& svg": { fontSize: 14 },
  },
  sendbar: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    borderRadius: 10,
    padding: "10px 12px",
    flexWrap: "wrap",
    border: "1px solid #F0DCB4",
    background: COLORS.amberSoft,
    "& .txt": {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12.5,
      color: COLORS.amber,
      flex: 1,
      minWidth: 150,
      lineHeight: 1.45,
    },
    "& .txt b": { fontWeight: 500 },
    "& svg": { fontSize: 16, flex: "0 0 auto" },
  },

  savedflash: {
    fontSize: 11.5,
    color: COLORS.green,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    opacity: 0,
    transition: "opacity .2s",
    "& svg": { fontSize: 14 },
  },
  savedflashOn: { opacity: 1 },

  /* completed */
  donehead: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  donetoggle: {
    border: 0,
    background: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    font: "500 15px 'DM Sans', sans-serif",
    color: COLORS.ink,
    padding: "4px 0",
    textTransform: "none",
  },
  donen: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11.5,
    background: COLORS.greenSoft,
    color: COLORS.green,
    borderRadius: 20,
    padding: "2px 8px",
    fontWeight: 500,
  },
  donesub: { fontSize: 12.5, color: COLORS.ink3 },

  doneSection: {
    marginTop: 34,
    paddingTop: 26,
    borderTop: `1px solid ${COLORS.line}`,
  },

  note: {
    marginTop: 20,
    fontSize: 12.5,
    color: COLORS.ink3,
    lineHeight: 1.6,
    maxWidth: 820,
    "& b": { color: COLORS.ink2, fontWeight: 500 },
  },
}));
